import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParamsData } from '../purchase-order/useSearchParamsData';
import { trpcSwr } from '../../../api/trpc';
import Toggle from 'rsuite/Toggle';
import { useSortingLocalStorage } from '../../../api/useSortingLocalStorage';
import { CustomsDeliveryOrderStatus } from '../../../export-types/cleaned-types';

export type OutletContextParams = {
  debouncedInternalId: string;
};

export const CustomsDeliveryOrdersTab = () => {
  const intl = useIntl();
  const { sortValues, toggleDirection, toggleLinked } =
    useSortingLocalStorage();
  const tabs = [
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.status-label.created',
        defaultMessage: 'Черновик',
        description: 'Статус доставки',
      }),
      path: 'created',
      name: 'created' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.status-label.preparing',
        defaultMessage: 'Новый',
        description: 'Статус доставки',
      }),
      path: 'preparing',
      name: 'preparing' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.inWork',
        defaultMessage: 'В работе',
        description: 'Надпись на вкладке',
      }),
      path: 'inWorkPreparing',
      name: 'inWorkPreparing' as const,
      statuses: ['codeSelected', 'calculationReady', 'protectCalculation'],
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.documents',
        defaultMessage: 'Документы',
        description: 'Надпись на вкладке',
      }),
      path: 'documents',
      name: 'documents' as const,
      statuses: ['prepareDocs', 'prepareInvoice', 'declarant'],
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.waitingForPayment',
        defaultMessage: 'Ожидает оплату',
        description: 'Надпись на вкладке',
      }),
      path: 'waitingForPayment',
      name: 'waitingForPayment' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.buyOut',
        defaultMessage: 'Выкупить',
        description: 'Надпись на вкладке',
      }),
      path: 'buyOut',
      name: 'buyOut' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.inWork',
        defaultMessage: 'В пути',
        description: 'Надпись на вкладке',
      }),
      path: 'inWork',
      name: 'inWork' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.inStock',
        defaultMessage: 'На складе',
        description: 'Надпись на вкладке',
      }),
      path: 'inStock',
      name: 'inStock' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.sentToRussia',
        defaultMessage: 'Отправлен в РФ',
        description: 'Надпись на вкладке',
      }),
      path: 'sentToRussia',
      name: 'sentToRussia' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.customs',
        defaultMessage: 'Таможня',
        description: 'Надпись на вкладке',
      }),
      path: 'customs',
      name: 'customs' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.transitWarehouse',
        defaultMessage: 'Транзитный склад',
        description: 'Надпись на вкладке',
      }),
      path: 'transitWarehouse',
      name: 'transitWarehouse' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.transferredToTC',
        defaultMessage: 'Переданные в ТК',
        description: 'Надпись на вкладке',
      }),
      path: 'transferredToTC',
      name: 'transferredToTC' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.received',
        defaultMessage: 'Полученные',
        description: 'Надпись на вкладке',
      }),
      path: 'received',
      name: 'received' as const,
    },
    {
      title: intl.formatMessage({
        id: 'customsDeliveries.tab-label.archived',
        defaultMessage: 'Архив',
        description: 'Надпись на вкладке',
      }),
      path: 'archived',
      name: 'archived' as const,
    },
  ];

  const [searchParamsData, setSearchParamsFunctions] = useSearchParamsData();

  const [internalId, setInternalId] = useState(
    searchParamsData['internal'] || '',
  );
  const { data } = trpcSwr.customsDelivery.totalByStatus.useSWR({
    searchValue: internalId,
    linked: sortValues.linked,
  });

  const context: OutletContextParams = {
    debouncedInternalId: internalId,
  };

  useEffect(() => {
    if (searchParamsData['internal'] !== internalId) {
      setSearchParamsFunctions['internal'](internalId);
    }
  }, [internalId, searchParamsData, setSearchParamsFunctions]);

  return (
    <>
      <div className="d-flex flex-wrap  justify-content-between align-items-center pb-4 ">
        <div className="d-flex w-75 mr-auto">
          <input
            className="form-control"
            style={{ width: '20%' }}
            type="text"
            placeholder="Поиск"
            value={searchParamsData['internal']}
            onChange={(event) => setInternalId(event.currentTarget.value)}
          />
        </div>
        <div className="d-flex align-items-center">
          <Toggle
            checked={sortValues.direction === 'desc'}
            onChange={toggleDirection}
          />
          <div className="ms-2">
            <FormattedMessage
              defaultMessage="Сначала новые"
              id="switchButton.label.newOnesFirst"
              description="Надпись на переключателе"
            />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <Toggle checked={sortValues.linked} onChange={toggleLinked} />
          <div className="ms-2">
            <FormattedMessage
              defaultMessage="Мои клиенты"
              id="switchButton.label.myCustomers"
              description="Надпись на переключателе"
            />
          </div>
        </div>
      </div>

      <ul className="nav nav-pills mb-4 d-inline-flex">
        {tabs.map((item) => {
          let count = 0;

          if (data) {
            if (item.statuses) {
              count = item.statuses.reduce((acc, status) => {
                const statusCount = data[status as CustomsDeliveryOrderStatus]
                  ? data[status as CustomsDeliveryOrderStatus]
                  : 0;
                return acc + statusCount;
              }, 0);
            } else {
              count = data
                ? data[item.name as CustomsDeliveryOrderStatus] || 0
                : 0;
            }
          }

          return (
            <li className="nav-item" key={item.title}>
              <NavLink to={item.path} className="nav-link text-decoration-none">
                {item.title} ({count})
              </NavLink>
            </li>
          );
        })}
      </ul>
      <Outlet context={context} />
    </>
  );
};
